import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Compass, Heart, Plus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import FaithJourneyStepper from "@/components/faith_journey/FaithJourneyStepper";
import ShareTestimonyModal from "@/components/faith_journey/ShareTestimonyModal";
import { useUiText } from "@/contexts/UiTextContext";

const CELEBRATION = {
  accepted_jesus_date: "Praise God — Decision For Jesus Recorded!",
  baptism_date: "Praise God — Baptism Recorded!",
  growth_track_date: "Praise God — Growth Track Complete!",
  forward_date: "Praise God — FORWARD Complete!",
};

export default function FaithJourney() {
  const { t } = useUiText();
  const [journey, setJourney] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGroup, setNewGroup] = useState({ group_name: "", dated_on: "" });
  const [newLeading, setNewLeading] = useState({ group_name: "", dated_on: "" });
  const [newTrip, setNewTrip] = useState({ destination: "", dated_on: "" });
  const [selectTeamId, setSelectTeamId] = useState("");
  const [selectJoinedOn, setSelectJoinedOn] = useState("");
  const [testimonies, setTestimonies] = useState([]);
  const [testimonyModal, setTestimonyModal] = useState({ open: false, context: "general" });

  const load = async () => {
    try {
      const [j, t2, m] = await Promise.all([
        api.get("/faith-journey/me"),
        api.get("/faith-journey/serve-teams"),
        api.get("/faith-journey/testimonies/mine"),
      ]);
      setJourney(j.data);
      setTeams(t2.data);
      setTestimonies(m.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "We couldn't load your journey.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setDate = async (field, value) => {
    try {
      const { data } = await api.put("/faith-journey/me", { [field]: value || "" });
      setJourney(data);
      if (value && CELEBRATION[field]) toast.success(CELEBRATION[field]);
      else if (!value) toast.success("Date cleared.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const joinTeam = async () => {
    if (!selectTeamId) return;
    try {
      await api.post("/faith-journey/me/serve-teams", {
        team_id: selectTeamId,
        joined_on: selectJoinedOn || null,
      });
      setSelectTeamId(""); setSelectJoinedOn("");
      toast.success("Praise God — Joined A Serve Team!");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const archiveServe = async (id) => {
    try {
      await api.post(`/faith-journey/me/serve-teams/${id}/archive`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const joinGroup = async () => {
    if (!newGroup.group_name.trim()) return;
    try {
      await api.post("/faith-journey/me/neighborhood-groups", {
        group_name: newGroup.group_name.trim(),
        dated_on: newGroup.dated_on || null,
      });
      setNewGroup({ group_name: "", dated_on: "" });
      toast.success("Praise God — Neighborhood Group Recorded!");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const archiveGroup = async (id) => {
    try {
      await api.post(`/faith-journey/me/neighborhood-groups/${id}/archive`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const startLeading = async () => {
    if (!newLeading.group_name.trim()) return;
    try {
      await api.post("/faith-journey/me/leading-groups", {
        group_name: newLeading.group_name.trim(),
        dated_on: newLeading.dated_on || null,
      });
      setNewLeading({ group_name: "", dated_on: "" });
      toast.success("Praise God — Now Leading A Group!");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const archiveLeading = async (id) => {
    try {
      await api.post(`/faith-journey/me/leading-groups/${id}/archive`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const addTrip = async () => {
    if (!newTrip.destination.trim()) return;
    try {
      await api.post("/faith-journey/me/missions-trips", {
        destination: newTrip.destination.trim(),
        dated_on: newTrip.dated_on || null,
      });
      setNewTrip({ destination: "", dated_on: "" });
      toast.success("Praise God — Missions Trip Logged!");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const archiveTrip = async (id) => {
    try {
      await api.post(`/faith-journey/me/missions-trips/${id}/archive`);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  if (loading) return <p className="italic text-charcoal-muted">Loading your journey…</p>;
  if (!journey) return null;

  const activeServeIds = new Set((journey.serve_teams || []).map((t) => t.team_id));
  const availableTeams = teams.filter((t) => !activeServeIds.has(t.id));

  return (
    <div className="space-y-6" data-testid="faith-journey-page">
      <section className="patria-card p-6 sm:p-8">
        <div className="flex items-start gap-3 flex-wrap justify-between">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
              <Heart className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
                {t("faith_journey.title", "My Faith Journey")}
              </p>
              <h1 className="mt-1 text-4xl sm:text-5xl font-serif text-forest-900">
                {t("faith_journey.hero", "Where God Is Meeting You")}
              </h1>
              <p className="mt-2 text-charcoal-soft max-w-2xl">
                {t("faith_journey.subtitle", "Add the dates as they come — in any order.")}
                {" "}Every milestone below can be recorded whenever it fits your story.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setTestimonyModal({ open: true, context: "general" })}
            className="patria-btn-secondary text-sm"
            data-testid="btn-share-testimony-general"
          >
            <Heart className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Share A Testimony
          </button>
        </div>
        <div className="mt-6">
          <FaithJourneyStepper journey={journey} />
        </div>
      </section>

      {/* Dated milestones */}
      <section className="patria-card p-6 space-y-4" data-testid="fj-editor-dates">
        <h2 className="text-2xl font-serif text-forest-900">Dated Milestones</h2>
        <p className="text-xs text-charcoal-muted italic">
          Enter dates in any order. Leave blank until you&rsquo;re ready — nothing is required.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { key: "accepted_jesus_date", label: "Accepted Jesus" },
            { key: "baptism_date", label: "Baptism" },
            { key: "growth_track_date", label: "Growth Track" },
            { key: "forward_date", label: "FORWARD", withShare: "forward" },
          ].map((f) => (
            <div key={f.key}>
              <label className="patria-label">{f.label}</label>
              <input
                type="date"
                className="patria-input w-full"
                value={journey[f.key] || ""}
                onChange={(e) => setDate(f.key, e.target.value)}
                data-testid={`fj-date-${f.key}`}
              />
              {f.withShare && journey[f.key] && (
                <button
                  type="button"
                  onClick={() => setTestimonyModal({ open: true, context: f.withShare })}
                  className="mt-1 text-xs text-terracotta-600 patria-link"
                  data-testid={`fj-share-${f.withShare}`}
                >
                  <Heart className="inline h-3 w-3 mr-1" strokeWidth={1.5} /> Share Your FORWARD Story
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Serve teams */}
      <section className="patria-card p-6 space-y-4" data-testid="fj-editor-serve-teams">
        <h2 className="text-2xl font-serif text-forest-900">Joining A Serve Team</h2>
        <p className="text-xs text-charcoal-muted italic">Ongoing — you can be on more than one team at a time.</p>
        <ul className="space-y-2" data-testid="fj-active-serve-teams">
          {(journey.serve_teams || []).length === 0 && (
            <li className="text-sm text-charcoal-muted italic">Not serving yet — pick a team below.</li>
          )}
          {(journey.serve_teams || []).map((t) => (
            <li key={t.id} className="p-3 rounded-xl bg-cream-100 flex items-center justify-between gap-2">
              <div>
                <p className="text-forest-900 font-medium">{t.team_name}</p>
                <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                  Joined {t.joined_on || "—"}
                </p>
              </div>
              <button
                onClick={() => archiveServe(t.id)}
                className="patria-btn-ghost text-terracotta-600 text-xs"
                data-testid={`fj-serve-archive-${t.id}`}
              >
                <X className="h-4 w-4 mr-1" strokeWidth={1.5} /> Left
              </button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select
            className="patria-input"
            value={selectTeamId}
            onChange={(e) => setSelectTeamId(e.target.value)}
            data-testid="fj-serve-select"
          >
            <option value="">Choose A Team…</option>
            {availableTeams.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <input
            type="date"
            className="patria-input"
            value={selectJoinedOn}
            onChange={(e) => setSelectJoinedOn(e.target.value)}
            data-testid="fj-serve-joined-on"
            placeholder="Joined on"
          />
          <button
            type="button"
            onClick={joinTeam}
            disabled={!selectTeamId}
            className="patria-btn-primary"
            data-testid="fj-serve-join-btn"
          >
            <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Join Team
          </button>
        </div>
      </section>

      {/* Neighborhood group */}
      <section className="patria-card p-6 space-y-4" data-testid="fj-editor-neighborhood">
        <h2 className="text-2xl font-serif text-forest-900">Joining A Neighborhood Group</h2>
        <ul className="space-y-2">
          {(journey.neighborhood_groups || []).length === 0 && (
            <li className="text-sm text-charcoal-muted italic">Not in a group yet — add one below when you are.</li>
          )}
          {(journey.neighborhood_groups || []).map((g) => (
            <li key={g.id} className="p-3 rounded-xl bg-cream-100 flex items-center justify-between gap-2">
              <div>
                <p className="text-forest-900 font-medium">{g.group_name}</p>
                <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                  Joined {g.dated_on || "—"}
                </p>
              </div>
              <button
                onClick={() => archiveGroup(g.id)}
                className="patria-btn-ghost text-terracotta-600 text-xs"
                data-testid={`fj-ng-archive-${g.id}`}
              >
                <X className="h-4 w-4 mr-1" strokeWidth={1.5} /> Left
              </button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            className="patria-input sm:col-span-2"
            placeholder="Group Name (e.g., The Vine — Rancho)"
            value={newGroup.group_name}
            onChange={(e) => setNewGroup({ ...newGroup, group_name: e.target.value })}
            data-testid="fj-ng-name"
          />
          <input
            type="date"
            className="patria-input"
            value={newGroup.dated_on}
            onChange={(e) => setNewGroup({ ...newGroup, dated_on: e.target.value })}
            data-testid="fj-ng-date"
          />
        </div>
        <button
          type="button"
          onClick={joinGroup}
          disabled={!newGroup.group_name.trim()}
          className="patria-btn-primary"
          data-testid="fj-ng-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Neighborhood Group
        </button>
      </section>

      {/* Leading a group */}
      <section className="patria-card p-6 space-y-4" data-testid="fj-editor-leading">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h2 className="text-2xl font-serif text-forest-900">Leading A Neighborhood Group</h2>
          {(journey.leading_groups || []).some((g) => !g.archived) && (
            <button
              type="button"
              onClick={() => setTestimonyModal({ open: true, context: "leading_group" })}
              className="text-xs text-terracotta-600 patria-link"
              data-testid="fj-share-leading_group"
            >
              <Heart className="inline h-3 w-3 mr-1" strokeWidth={1.5} /> Share Your Leadership Story
            </button>
          )}
        </div>
        <ul className="space-y-2">
          {(journey.leading_groups || []).length === 0 && (
            <li className="text-sm text-charcoal-muted italic">Not leading a group yet — add one when the time comes.</li>
          )}
          {(journey.leading_groups || []).map((g) => (
            <li key={g.id} className="p-3 rounded-xl bg-cream-100 flex items-center justify-between gap-2">
              <div>
                <p className="text-forest-900 font-medium">{g.group_name}</p>
                <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                  Started {g.dated_on || "—"}
                </p>
              </div>
              <button
                onClick={() => archiveLeading(g.id)}
                className="patria-btn-ghost text-terracotta-600 text-xs"
                data-testid={`fj-lead-archive-${g.id}`}
              >
                <X className="h-4 w-4 mr-1" strokeWidth={1.5} /> Stopped
              </button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            className="patria-input sm:col-span-2"
            placeholder="Group You're Leading"
            value={newLeading.group_name}
            onChange={(e) => setNewLeading({ ...newLeading, group_name: e.target.value })}
            data-testid="fj-lead-name"
          />
          <input
            type="date"
            className="patria-input"
            value={newLeading.dated_on}
            onChange={(e) => setNewLeading({ ...newLeading, dated_on: e.target.value })}
            data-testid="fj-lead-date"
          />
        </div>
        <button
          type="button"
          onClick={startLeading}
          disabled={!newLeading.group_name.trim()}
          className="patria-btn-primary"
          data-testid="fj-lead-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Group I&rsquo;m Leading
        </button>
      </section>

      {/* Missions trips (side badge) */}
      <section className="patria-card p-6 space-y-4" data-testid="fj-editor-trips">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-terracotta-500 text-white flex items-center justify-center">
            <Compass className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-2xl font-serif text-forest-900">Missions Trips</h2>
            <p className="text-xs text-charcoal-muted italic">
              Log every trip — a side badge on your journey card. Order doesn&rsquo;t matter.
            </p>
          </div>
        </div>
        <ul className="space-y-2">
          {(journey.missions_trips || []).length === 0 && (
            <li className="text-sm text-charcoal-muted italic">No trips logged yet.</li>
          )}
          {(journey.missions_trips || []).map((t) => (
            <li key={t.id} className="p-3 rounded-xl bg-terracotta-500/5 flex items-center justify-between gap-2">
              <div>
                <p className="text-forest-900 font-medium">{t.destination}</p>
                <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                  {t.dated_on || "Undated"}
                </p>
              </div>
              <button
                onClick={() => archiveTrip(t.id)}
                className="patria-btn-ghost text-terracotta-600 text-xs"
                data-testid={`fj-trip-archive-${t.id}`}
              >
                <X className="h-4 w-4 mr-1" strokeWidth={1.5} /> Remove
              </button>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            className="patria-input sm:col-span-2"
            placeholder="Destination (e.g., Baja California)"
            value={newTrip.destination}
            onChange={(e) => setNewTrip({ ...newTrip, destination: e.target.value })}
            data-testid="fj-trip-destination"
          />
          <input
            type="date"
            className="patria-input"
            value={newTrip.dated_on}
            onChange={(e) => setNewTrip({ ...newTrip, dated_on: e.target.value })}
            data-testid="fj-trip-date"
          />
        </div>
        <button
          type="button"
          onClick={addTrip}
          disabled={!newTrip.destination.trim()}
          className="patria-btn-primary"
          data-testid="fj-trip-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Log A Missions Trip
        </button>
      </section>

      {/* My Testimonies */}
      <section className="patria-card p-6 space-y-3" data-testid="fj-testimonies-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-serif text-forest-900">My Stories</h2>
            <p className="text-xs text-charcoal-muted italic">
              Testimonies you&rsquo;ve shared. Edit anytime — only PO+ sees the aggregated Praise Report.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setTestimonyModal({ open: true, context: "general" })}
            className="patria-btn-primary text-sm"
            data-testid="btn-share-testimony-list"
          >
            <Heart className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Share A Testimony
          </button>
        </div>
        {testimonies.length === 0 && (
          <p className="text-sm italic text-charcoal-muted" data-testid="fj-testimonies-empty">
            No stories shared yet — every journey has one worth celebrating.
          </p>
        )}
        <ul className="space-y-2" data-testid="fj-testimonies-list">
          {testimonies.map((s) => (
            <li key={s.id} className="p-3 rounded-xl bg-terracotta-500/5 border-l-2 border-terracotta-500" data-testid={`fj-testimony-${s.id}`}>
              <p className="italic text-forest-900">&ldquo;{s.text}&rdquo;</p>
              <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-1">
                {s.dated_on || "—"}
                {s.context && s.context !== "general" && ` · ${s.context.replaceAll("_", " ")}`}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <ShareTestimonyModal
        open={testimonyModal.open}
        context={testimonyModal.context}
        onClose={() => setTestimonyModal({ open: false, context: "general" })}
        onSaved={(saved) => setTestimonies((prev) => [saved, ...prev])}
      />
    </div>
  );
}
